import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, X, Heart, ExternalLink, ThumbsUp } from "lucide-react";
import { Button } from "./ui/button";

// Configuration - À activer quand l'app sera publiée sur Google Play
const PLAY_STORE_CONFIG = {
  enabled: false, // Mettre à true quand l'app est publiée
  url: "https://play.google.com/store/apps/details?id=com.loveconnect.app", // Remplacer par la vraie URL
};

export default function ReviewPromptModal({ 
  isOpen, 
  onClose, 
  onReviewGiven,
  language = 'fr',
  triggerSource = 'general' // 'match', 'credits', 'usage'
}) {
  const [step, setStep] = useState('ask'); // 'ask', 'positive', 'negative', 'thanks'
  const [selectedRating, setSelectedRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);

  const translations = {
    fr: {
      // Step 1: Initial ask
      askTitle: "Vous aimez Love Connect + ?",
      askSubtitle: triggerSource === 'match' 
        ? "Félicitations pour votre match ! 🎉"
        : triggerSource === 'credits'
        ? "Merci pour votre achat ! 💎"
        : "Vous utilisez Love Connect + depuis un moment maintenant",
      askQuestion: "Comment évaluez-vous votre expérience ?",
      
      // Step 2a: Positive feedback
      positiveTitle: "Merci beaucoup ! 🥰",
      positiveSubtitle: "Votre avis compte énormément pour nous",
      positiveAction: PLAY_STORE_CONFIG.enabled 
        ? "Donner mon avis sur Google Play"
        : "Merci pour votre soutien !",
      positiveLater: "Plus tard",
      
      // Step 2b: Negative feedback
      negativeTitle: "Merci pour votre retour",
      negativeSubtitle: "Nous sommes désolés que votre expérience ne soit pas parfaite",
      negativeQuestion: "Comment pouvons-nous nous améliorer ?",
      negativePlaceholder: "Dites-nous ce qui pourrait être mieux...",
      negativeSend: "Envoyer",
      negativeSkip: "Passer",
      
      // Step 3: Thanks
      thanksTitle: "Merci ! ❤️",
      thanksSubtitle: "Votre avis nous aide à améliorer Love Connect +",
      thanksClose: "Fermer",
      
      // Common
      notNow: "Pas maintenant",
    },
    en: {
      askTitle: "Do you like Love Connect +?",
      askSubtitle: triggerSource === 'match'
        ? "Congratulations on your match! 🎉"
        : triggerSource === 'credits'
        ? "Thanks for your purchase! 💎"
        : "You've been using Love Connect + for a while now",
      askQuestion: "How would you rate your experience?",
      
      positiveTitle: "Thank you so much! 🥰",
      positiveSubtitle: "Your review means a lot to us",
      positiveAction: PLAY_STORE_CONFIG.enabled
        ? "Leave a review on Google Play"
        : "Thanks for your support!",
      positiveLater: "Later",
      
      negativeTitle: "Thanks for your feedback",
      negativeSubtitle: "We're sorry your experience isn't perfect",
      negativeQuestion: "How can we improve?",
      negativePlaceholder: "Tell us what could be better...",
      negativeSend: "Send",
      negativeSkip: "Skip",
      
      thanksTitle: "Thank you! ❤️",
      thanksSubtitle: "Your feedback helps us improve Love Connect +",
      thanksClose: "Close",
      
      notNow: "Not now",
    }
  };

  const t = translations[language] || translations.fr;

  const handleRatingClick = (rating) => {
    setSelectedRating(rating);
    
    // Délai pour montrer la sélection avant de passer à l'étape suivante
    setTimeout(() => {
      if (rating >= 4) {
        setStep('positive');
      } else {
        setStep('negative');
      }
    }, 300);
  };

  const handlePlayStoreClick = () => {
    if (PLAY_STORE_CONFIG.enabled && PLAY_STORE_CONFIG.url) {
      window.open(PLAY_STORE_CONFIG.url, '_blank');
    }
    
    // Enregistrer que l'utilisateur a donné un avis
    if (onReviewGiven) {
      onReviewGiven({ rating: selectedRating, action: 'play_store' });
    }
    
    setStep('thanks');
  };

  const handleFeedbackSubmit = (feedback) => {
    // Enregistrer le feedback négatif
    if (onReviewGiven) {
      onReviewGiven({ rating: selectedRating, action: 'feedback', feedback });
    }
    
    setStep('thanks');
  };

  const handleClose = () => {
    // Enregistrer que l'utilisateur a fermé sans donner d'avis
    if (onReviewGiven && step !== 'thanks') {
      onReviewGiven({ rating: selectedRating || 0, action: 'dismissed' });
    }
    
    // Reset state
    setStep('ask');
    setSelectedRating(0);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="relative w-full max-w-sm bg-card rounded-2xl shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-all z-10"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Step 1: Ask for rating */}
          {step === 'ask' && (
            <div className="p-6 text-center">
              {/* Icon */}
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-primary to-pink-600 flex items-center justify-center">
                <Heart className="w-8 h-8 text-white fill-white" />
              </div>

              <h3 className="text-xl font-bold text-white mb-1">{t.askTitle}</h3>
              <p className="text-sm text-white/60 mb-4">{t.askSubtitle}</p>
              <p className="text-white/80 mb-4">{t.askQuestion}</p>

              {/* Star Rating */}
              <div className="flex justify-center gap-2 mb-6">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => handleRatingClick(star)}
                    onMouseEnter={() => setHoveredRating(star)}
                    onMouseLeave={() => setHoveredRating(0)}
                    className="p-1 transition-transform hover:scale-110 active:scale-95"
                  >
                    <Star
                      className={`w-10 h-10 transition-colors ${
                        star <= (hoveredRating || selectedRating)
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-white/30'
                      }`}
                    />
                  </button>
                ))}
              </div>

              <button
                onClick={handleClose}
                className="text-sm text-white/50 hover:text-white/70 transition-colors"
              >
                {t.notNow}
              </button>
            </div>
          )}

          {/* Step 2a: Positive feedback - Redirect to Play Store */}
          {step === 'positive' && (
            <div className="p-6 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                <ThumbsUp className="w-8 h-8 text-white" />
              </div>

              <h3 className="text-xl font-bold text-white mb-1">{t.positiveTitle}</h3>
              <p className="text-sm text-white/60 mb-6">{t.positiveSubtitle}</p>

              {/* Stars recap */}
              <div className="flex justify-center gap-1 mb-6">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-6 h-6 ${
                      star <= selectedRating
                        ? 'text-yellow-400 fill-yellow-400'
                        : 'text-white/30'
                    }`}
                  />
                ))}
              </div>

              <Button
                onClick={handlePlayStoreClick}
                className="w-full mb-3 bg-gradient-to-r from-primary to-pink-600 hover:opacity-90"
              >
                {PLAY_STORE_CONFIG.enabled && (
                  <ExternalLink className="w-4 h-4 mr-2" />
                )}
                {t.positiveAction}
              </Button>

              {PLAY_STORE_CONFIG.enabled && (
                <button
                  onClick={() => setStep('thanks')}
                  className="text-sm text-white/50 hover:text-white/70 transition-colors"
                >
                  {t.positiveLater}
                </button>
              )}
            </div>
          )}

          {/* Step 2b: Negative feedback - Internal feedback form */}
          {step === 'negative' && (
            <NegativeFeedbackForm
              t={t}
              selectedRating={selectedRating}
              onSubmit={handleFeedbackSubmit}
              onSkip={() => setStep('thanks')}
            />
          )}

          {/* Step 3: Thanks */}
          {step === 'thanks' && (
            <div className="p-6 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-primary to-pink-600 flex items-center justify-center">
                <Heart className="w-8 h-8 text-white fill-white animate-pulse" />
              </div>

              <h3 className="text-xl font-bold text-white mb-1">{t.thanksTitle}</h3>
              <p className="text-sm text-white/60 mb-6">{t.thanksSubtitle}</p>

              <Button
                onClick={handleClose}
                variant="outline"
                className="w-full"
              >
                {t.thanksClose}
              </Button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// Sous-composant pour le formulaire de feedback négatif
function NegativeFeedbackForm({ t, selectedRating, onSubmit, onSkip }) {
  const [feedback, setFeedback] = useState('');

  return (
    <div className="p-6 text-center">
      <h3 className="text-xl font-bold text-white mb-1">{t.negativeTitle}</h3>
      <p className="text-sm text-white/60 mb-4">{t.negativeSubtitle}</p>

      {/* Stars recap */}
      <div className="flex justify-center gap-1 mb-4">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-5 h-5 ${
              star <= selectedRating
                ? 'text-yellow-400 fill-yellow-400'
                : 'text-white/30'
            }`}
          />
        ))}
      </div>

      <p className="text-sm text-white/80 mb-3">{t.negativeQuestion}</p>

      <textarea
        value={feedback}
        onChange={(e) => setFeedback(e.target.value)}
        placeholder={t.negativePlaceholder}
        className="w-full h-24 px-3 py-2 mb-4 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
      />

      <div className="flex gap-3">
        <Button
          onClick={onSkip}
          variant="outline"
          className="flex-1"
        >
          {t.negativeSkip}
        </Button>
        <Button
          onClick={() => onSubmit(feedback)}
          className="flex-1 bg-gradient-to-r from-primary to-pink-600"
          disabled={!feedback.trim()}
        >
          {t.negativeSend}
        </Button>
      </div>
    </div>
  );
}

// Export de la configuration pour pouvoir la modifier facilement
export { PLAY_STORE_CONFIG };
